import { useMemo } from "react";

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { getLatestFileMetas7 } from "@synthesis/shared-services/filesystem-gge/documents/client";
import { logger } from "@synthesis/shared-utils";

const UUID_PATTERN = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

export const isFileId = (value: string) => UUID_PATTERN.test(value);

export const ifcNameQueryKey = (id: string) => ["filesystem", "ifc-name", id] as const;

/**
 * Loads IFC file display names by document id via POST /document/meta/latest.
 * Batch-fetches missing ids, stores each name under `ifcNameQueryKey(id)` for cache reuse.
 */
export const useIfcNames = (fileIds: string[]) => {
  const queryClient = useQueryClient();

  const ids = useMemo(() => [...new Set(fileIds.filter((id) => Boolean(id) && isFileId(id)))].toSorted(), [fileIds]);

  useQuery({
    queryKey: ["filesystem", "ifc-names-batch", ids],
    queryFn: async ({ signal }) => {
      const missing = ids.filter((id) => queryClient.getQueryData(ifcNameQueryKey(id)) === undefined);
      if (missing.length === 0) return true;

      try {
        const metas = (await getLatestFileMetas7({ body: { ids: missing }, signal })).data;
        const nameByPossibleId = new Map<string, string>();

        metas.forEach((meta) => {
          if (!meta.baseName) return;
          nameByPossibleId.set(meta.id, meta.baseName);
          if (meta.versionParentId) {
            nameByPossibleId.set(meta.versionParentId, meta.baseName);
          }
        });

        missing.forEach((id, index) => {
          const fromMap = nameByPossibleId.get(id);
          if (fromMap !== undefined) {
            queryClient.setQueryData(ifcNameQueryKey(id), fromMap);
            return;
          }

          if (metas.length === missing.length && metas[index]?.baseName) {
            queryClient.setQueryData(ifcNameQueryKey(id), metas[index]!.baseName);
            return;
          }

          queryClient.setQueryData(ifcNameQueryKey(id), null);
        });
      } catch (error) {
        logger.error("Error fetching IFC file names:", error);
        missing.forEach((id) => {
          if (queryClient.getQueryData(ifcNameQueryKey(id)) === undefined) {
            queryClient.setQueryData(ifcNameQueryKey(id), null);
          }
        });
      }

      return true;
    },
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const nameQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ifcNameQueryKey(id),
      queryFn: async (): Promise<string | null> => null,
      enabled: false,
      staleTime: 10 * 60 * 1000,
    })),
  });

  const ifcNamesById = useMemo(() => {
    const map: Record<string, string | null> = {};
    ids.forEach((id, index) => {
      const fromQuery = nameQueries[index]?.data;
      const fromCache = queryClient.getQueryData<string | null>(ifcNameQueryKey(id));
      if (fromCache !== undefined) {
        map[id] = fromCache;
      } else if (fromQuery !== undefined) {
        map[id] = fromQuery;
      }
    });
    return map;
  }, [ids, nameQueries, queryClient]);

  return { ifcNamesById, ifcIds: ids };
};
